import React, { useState, useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    Tooltip,
    TextField,
    Collapse,
    Divider,
    Grid,
} from '@mui/material';
import {
    AutoAwesome,
    Flight,
    Hotel,
    DirectionsCar,
    Attractions,
    AccountBalanceWallet,
    Lightbulb,
    Check,
    Close,
    Edit,
    Delete,
    ExpandMore,
    ExpandLess,
    CheckCircle,
    RadioButtonUnchecked,
    Info,
    TipsAndUpdates,
    SelectAll,
    DeselectOutlined,
} from '@mui/icons-material';
import { formatDate, daysBetween } from '../utils/dateHelpers';

// ─── Types ───────────────────────────────────────────

export interface AiFlightItem {
    title: string;
    from: string;
    to: string;
    departDate: string;
    returnDate?: string;
    cabinClass: string;
    estimatedCost: number;
    notes: string;
    aiReason: string;
}

export interface AiHotelItem {
    title: string;
    location: string;
    checkIn: string;
    checkOut: string;
    propertyType: string;
    starRating: number;
    estimatedCostPerNight: number;
    amenities: string[];
    notes: string;
    aiReason: string;
}

export interface AiCarItem {
    title: string;
    location: string;
    pickupDate: string;
    dropoffDate: string;
    carType: string;
    estimatedCostPerDay: number;
    notes: string;
    aiReason: string;
}

export interface AiActivityItem {
    title: string;
    category: string;
    date: string;
    time: string;
    duration: string;
    location: string;
    estimatedCostPerPerson: number;
    notes: string;
    aiReason: string;
}

export interface AiItineraryDay {
    dayNumber: number;
    date: string;
    theme: string;
    morning?: string;
    afternoon?: string;
    evening?: string;
}

export interface AiBudget {
    flights: number;
    hotels: number;
    carRental: number;
    activities: number;
    foodEstimate: number;
    miscEstimate: number;
    total: number;
    savingsTips: string[];
}

export interface AiTripPlan {
    title: string;
    description: string;
    flights: AiFlightItem[];
    hotels: AiHotelItem[];
    cars?: AiCarItem[] | null;
    activities: AiActivityItem[];
    itinerary: AiItineraryDay[];
    budget: AiBudget;
    travelTips: string[];
}

// Selection state per item
type ItemSelection = {
    selected: boolean;
    editing: boolean;
    editData: any; // mutable copy for inline editing
};

interface AiResultsReviewModalProps {
    open: boolean;
    onClose: () => void;
    plan: AiTripPlan | null;
    onApply: (selectedPlan: AiTripPlan) => void;
    mode?: 'phase1' | 'phase2'; // phase2 = activities only
}

// ─── Helpers ─────────────────────────────────────────


const formatCurrency = (amount: number): string => {
    if (!amount && amount !== 0) return '';
    return `$${amount.toLocaleString()}`;
};

const categoryIcons: Record<string, string> = {
    tour: '🗺️', adventure: '🏔️', food: '🍽️', cultural: '🏛️',
    wellness: '🧘', nightlife: '🌙', shopping: '🛍️', sports: '⚽', other: '📌',
};

// ─── Section Colors (matching CreateTripPage) ────────

const sectionColors = {
    flights: '#2196F3',
    hotels: '#4A6FA5',
    cars: '#607D8B',
    activities: '#2D6E6A',
    budget: '#FF9800',
    tips: '#795548',
};

// ─── Main Component ──────────────────────────────────

const AiResultsReviewModal: React.FC<AiResultsReviewModalProps> = ({
    open,
    onClose,
    plan,
    onApply,
    mode = 'phase1',
}) => {
    // ─── Selection State ─────────────────────────────
    const [flightSelections, setFlightSelections] = useState<ItemSelection[]>([]);
    const [hotelSelections, setHotelSelections] = useState<ItemSelection[]>([]);
    const [carSelections, setCarSelections] = useState<ItemSelection[]>([]);
    const [activitySelections, setActivitySelections] = useState<ItemSelection[]>([]);
    const [expandedSections, setExpandedSections] = useState<string[]>(
        mode === 'phase1' ? ['flights', 'hotels', 'cars', 'activities', 'budget'] : ['activities']    );

    // Initialize selections when plan changes
    React.useEffect(() => {
        if (!plan) return;
        setFlightSelections(plan.flights.map(f => ({ selected: true, editing: false, editData: { ...f } })));
        setHotelSelections(plan.hotels.map(h => ({ selected: true, editing: false, editData: { ...h } })));
        setCarSelections((plan.cars || []).map(c => ({ selected: true, editing: false, editData: { ...c } })));
        setActivitySelections(plan.activities.map(a => ({ selected: true, editing: false, editData: { ...a } })));
        setExpandedSections(mode === 'phase1' ? ['flights', 'hotels', 'cars', 'budget'] : ['activities']);
    }, [plan, mode]);

    // ─── Toggle Helpers ──────────────────────────────

    const toggleSection = (section: string) => {
        setExpandedSections(prev =>
            prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]
        );
    };

    const toggleItem = useCallback((
        setSelections: React.Dispatch<React.SetStateAction<ItemSelection[]>>,
        idx: number,
    ) => {
        setSelections(prev => prev.map((s, i) =>
            i === idx ? { ...s, selected: !s.selected } : s
        ));
    }, []);

    const toggleEdit = useCallback((
        setSelections: React.Dispatch<React.SetStateAction<ItemSelection[]>>,
        idx: number,
    ) => {
        setSelections(prev => prev.map((s, i) =>
            i === idx ? { ...s, editing: !s.editing } : s
        ));
    }, []);

    const updateEditField = useCallback((
        setSelections: React.Dispatch<React.SetStateAction<ItemSelection[]>>,
        idx: number,
        field: string,
        value: any,
    ) => {
        setSelections(prev => prev.map((s, i) =>
            i === idx ? { ...s, editData: { ...s.editData, [field]: value } } : s
        ));
    }, []);

    const removeItem = useCallback((
        setSelections: React.Dispatch<React.SetStateAction<ItemSelection[]>>,
        idx: number,
    ) => {
        setSelections(prev => prev.map((s, i) =>
            i === idx ? { ...s, selected: false, editing: false } : s
        ));
    }, []);

    // ─── Select All / Deselect All ──────────────────

    const selectedCount = [
        ...flightSelections,
        ...hotelSelections,
        ...carSelections,
        ...activitySelections,
    ].filter(s => s.selected).length;

    const totalCount = flightSelections.length + hotelSelections.length +
        carSelections.length + activitySelections.length;

    const selectAll = () => {
        setFlightSelections(prev => prev.map(s => ({ ...s, selected: true })));
        setHotelSelections(prev => prev.map(s => ({ ...s, selected: true })));
        setCarSelections(prev => prev.map(s => ({ ...s, selected: true })));
        setActivitySelections(prev => prev.map(s => ({ ...s, selected: true })));
    };

    const deselectAll = () => {
        setFlightSelections(prev => prev.map(s => ({ ...s, selected: false })));
        setHotelSelections(prev => prev.map(s => ({ ...s, selected: false })));
        setCarSelections(prev => prev.map(s => ({ ...s, selected: false })));
        setActivitySelections(prev => prev.map(s => ({ ...s, selected: false })));
    };

    // ─── Apply Handler ───────────────────────────────

    const handleApply = () => {
        if (!plan) return;

        const selectedPlan: AiTripPlan = {
            ...plan,
            flights: flightSelections
                .filter(s => s.selected)
                .map(s => s.editData),
            hotels: hotelSelections
                .filter(s => s.selected)
                .map(s => s.editData),
            cars: carSelections
                .filter(s => s.selected)
                .map(s => s.editData),
            activities: activitySelections
                .filter(s => s.selected)
                .map(s => s.editData),
        };

        onApply(selectedPlan);
    };

    if (!plan) return null;

    // ─── Estimated Total ─────────────────────────────

    const estimatedTotal = [
        ...flightSelections.filter(s => s.selected).map(s => s.editData.estimatedCost || 0),
        ...hotelSelections.filter(s => s.selected).map(s => {
            const nights = s.editData.checkIn && s.editData.checkOut
                ? Math.max(0, daysBetween(s.editData.checkIn, s.editData.checkOut) - 1)
                : 1;
            return (s.editData.estimatedCostPerNight || 0) * Math.max(nights, 1);
        }),
        ...carSelections.filter(s => s.selected).map(s => {
            const days = s.editData.pickupDate && s.editData.dropoffDate
                ? Math.max(0, daysBetween(s.editData.pickupDate, s.editData.dropoffDate) - 1)
                : 1;
            return (s.editData.estimatedCostPerDay || 0) * Math.max(days, 1);
        }),
        ...activitySelections.filter(s => s.selected).map(s => s.editData.estimatedCostPerPerson || 0),
    ].reduce((a, b) => a + b, 0);

    // ═══════════════════════════════════════════════════
    // RENDER
    // ═══════════════════════════════════════════════════

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            scroll="paper"
            PaperProps={{
                sx: {
                    borderRadius: 3,
                    overflow: 'hidden',
                    maxHeight: '90vh',
                },
            }}
        >
            {/* Top gradient bar */}
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #FF6B35 0%, #2D6E6A 50%, #2196F3 100%)' }} />

            {/* Header */}
            <DialogTitle sx={{ pb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 40, height: 40, borderRadius: 2,
                            background: 'linear-gradient(135deg, #FF6B35, #E55A2B)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <AutoAwesome sx={{ color: 'white', fontSize: 22 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: 'Poppins, sans-serif', lineHeight: 1.2 }}>
                                {mode === 'phase1' ? 'AI Trip Plan Review' : 'Activity Recommendations'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {selectedCount} of {totalCount} items selected · Est. {formatCurrency(estimatedTotal)}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton onClick={onClose} size="small">
                        <Close />
                    </IconButton>
                </Box>
            </DialogTitle>

            {/* Trip Title & Description */}
            <Box sx={{ px: 3, pb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    ✨ {plan.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {plan.description}
                </Typography>

                {/* Select All / Deselect All */}
                <Box sx={{ display: 'flex', gap: 1, mt: 1.5 }}>
                    <Button size="small" startIcon={<SelectAll />} onClick={selectAll}
                        disabled={selectedCount === totalCount}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#2D6E6A' }}>
                        Select All
                    </Button>
                    <Button size="small" startIcon={<DeselectOutlined />} onClick={deselectAll}
                        disabled={selectedCount === 0}
                        sx={{ textTransform: 'none', fontSize: '0.75rem', color: '#999' }}>
                        Deselect All
                    </Button>
                </Box>
            </Box>

            <Divider />

            <DialogContent sx={{ px: 0, py: 0 }}>

                {/* ═══ FLIGHTS SECTION ═══════════════════════ */}
                {plan.flights.length > 0 && (mode === 'phase1' || mode === 'phase2') && (
                    <SectionWrapper
                        title="Flights"
                        icon={<Flight />}
                        color={sectionColors.flights}
                        count={flightSelections.filter(s => s.selected).length}
                        total={plan.flights.length}
                        expanded={expandedSections.includes('flights')}
                        onToggle={() => toggleSection('flights')}
                    >
                        {plan.flights.map((flight, idx) => (
                            <ReviewItemCard
                                key={`flight-${idx}`}
                                selected={flightSelections[idx]?.selected ?? true}
                                editing={flightSelections[idx]?.editing ?? false}
                                onToggle={() => toggleItem(setFlightSelections, idx)}
                                onEdit={() => toggleEdit(setFlightSelections, idx)}
                                onRemove={() => removeItem(setFlightSelections, idx)}
                                color={sectionColors.flights}
                                aiReason={flight.aiReason}
                                renderDisplay={() => (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            ✈️ {flight.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {flight.from} → {flight.to} · {flight.cabinClass}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(flight.departDate)}
                                            {flight.returnDate ? ` – ${formatDate(flight.returnDate)}` : ' (one-way)'}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: sectionColors.flights, mt: 0.5 }}>
                                            {formatCurrency(flight.estimatedCost)}
                                        </Typography>
                                        {flight.notes && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {flight.notes}
                                            </Typography>
                                        )}
                                    </>
                                )}
                                renderEdit={() => (
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={12}>
                                            <TextField fullWidth size="small" label="Title"
                                                value={flightSelections[idx]?.editData?.title || ''}
                                                onChange={(e) => updateEditField(setFlightSelections, idx, 'title', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" label="From"
                                                value={flightSelections[idx]?.editData?.from || ''}
                                                onChange={(e) => updateEditField(setFlightSelections, idx, 'from', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" label="To"
                                                value={flightSelections[idx]?.editData?.to || ''}
                                                onChange={(e) => updateEditField(setFlightSelections, idx, 'to', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField fullWidth size="small" label="Cabin"
                                                value={flightSelections[idx]?.editData?.cabinClass || ''}
                                                onChange={(e) => updateEditField(setFlightSelections, idx, 'cabinClass', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField fullWidth size="small" label="Est. Cost" type="number"
                                                value={flightSelections[idx]?.editData?.estimatedCost || ''}
                                                onChange={(e) => updateEditField(setFlightSelections, idx, 'estimatedCost', parseFloat(e.target.value) || 0)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField fullWidth size="small" label="Notes"
                                                value={flightSelections[idx]?.editData?.notes || ''}
                                                onChange={(e) => updateEditField(setFlightSelections, idx, 'notes', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                    </Grid>
                                )}
                            />
                        ))}
                    </SectionWrapper>
                )}

                {/* ═══ HOTELS SECTION ════════════════════════ */}
                {plan.hotels.length > 0 && (mode === 'phase1' || mode === 'phase2') && (
                    <SectionWrapper
                        title="Hotels"
                        icon={<Hotel />}
                        color={sectionColors.hotels}
                        count={hotelSelections.filter(s => s.selected).length}
                        total={plan.hotels.length}
                        expanded={expandedSections.includes('hotels')}
                        onToggle={() => toggleSection('hotels')}
                    >
                        {plan.hotels.map((hotel, idx) => (
                            <ReviewItemCard
                                key={`hotel-${idx}`}
                                selected={hotelSelections[idx]?.selected ?? true}
                                editing={hotelSelections[idx]?.editing ?? false}
                                onToggle={() => toggleItem(setHotelSelections, idx)}
                                onEdit={() => toggleEdit(setHotelSelections, idx)}
                                onRemove={() => removeItem(setHotelSelections, idx)}
                                color={sectionColors.hotels}
                                aiReason={hotel.aiReason}
                                renderDisplay={() => (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            🏨 {hotel.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {hotel.location} · {hotel.propertyType} · {'★'.repeat(hotel.starRating || 0)}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(hotel.checkIn)} – {formatDate(hotel.checkOut)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: sectionColors.hotels, mt: 0.5 }}>
                                            {formatCurrency(hotel.estimatedCostPerNight)}/night
                                        </Typography>
                                        {hotel.amenities && hotel.amenities.length > 0 && (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                                {hotel.amenities.map(a => (
                                                    <Chip key={a} label={a} size="small"
                                                        sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#F3E5F5', color: '#3D5A8A' }} />
                                                ))}
                                            </Box>
                                        )}
                                        {hotel.notes && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {hotel.notes}
                                            </Typography>
                                        )}
                                    </>
                                )}
                                renderEdit={() => (
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={8}>
                                            <TextField fullWidth size="small" label="Hotel Name"
                                                value={hotelSelections[idx]?.editData?.title || ''}
                                                onChange={(e) => updateEditField(setHotelSelections, idx, 'title', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField fullWidth size="small" label="Stars" type="number"
                                                value={hotelSelections[idx]?.editData?.starRating || ''}
                                                onChange={(e) => updateEditField(setHotelSelections, idx, 'starRating', parseInt(e.target.value) || 0)}
                                                inputProps={{ min: 1, max: 5 }}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" label="Location"
                                                value={hotelSelections[idx]?.editData?.location || ''}
                                                onChange={(e) => updateEditField(setHotelSelections, idx, 'location', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" label="$/night" type="number"
                                                value={hotelSelections[idx]?.editData?.estimatedCostPerNight || ''}
                                                onChange={(e) => updateEditField(setHotelSelections, idx, 'estimatedCostPerNight', parseFloat(e.target.value) || 0)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth size="small" label="Notes"
                                                value={hotelSelections[idx]?.editData?.notes || ''}
                                                onChange={(e) => updateEditField(setHotelSelections, idx, 'notes', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                    </Grid>
                                )}
                            />
                        ))}
                    </SectionWrapper>
                )}

                {/* ═══ CARS SECTION ══════════════════════════ */}
                {plan.cars && plan.cars.length > 0 && (mode === 'phase1' || mode === 'phase2') && (
                    <SectionWrapper
                        title="Car Rental"
                        icon={<DirectionsCar />}
                        color={sectionColors.cars}
                        count={carSelections.filter(s => s.selected).length}
                        total={(plan.cars || []).length}
                        expanded={expandedSections.includes('cars')}
                        onToggle={() => toggleSection('cars')}
                    >
                        {(plan.cars || []).map((car, idx) => (
                            <ReviewItemCard
                                key={`car-${idx}`}
                                selected={carSelections[idx]?.selected ?? true}
                                editing={carSelections[idx]?.editing ?? false}
                                onToggle={() => toggleItem(setCarSelections, idx)}
                                onEdit={() => toggleEdit(setCarSelections, idx)}
                                onRemove={() => removeItem(setCarSelections, idx)}
                                color={sectionColors.cars}
                                aiReason={car.aiReason}
                                renderDisplay={() => (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            🚗 {car.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {car.location} · {car.carType}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {formatDate(car.pickupDate)} – {formatDate(car.dropoffDate)}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: sectionColors.cars, mt: 0.5 }}>
                                            {formatCurrency(car.estimatedCostPerDay)}/day
                                        </Typography>
                                        {car.notes && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {car.notes}
                                            </Typography>
                                        )}
                                    </>
                                )}
                                renderEdit={() => (
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={6}>
                                            <TextField fullWidth size="small" label="Company"
                                                value={carSelections[idx]?.editData?.title || ''}
                                                onChange={(e) => updateEditField(setCarSelections, idx, 'title', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField fullWidth size="small" label="Car Type"
                                                value={carSelections[idx]?.editData?.carType || ''}
                                                onChange={(e) => updateEditField(setCarSelections, idx, 'carType', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField fullWidth size="small" label="$/day" type="number"
                                                value={carSelections[idx]?.editData?.estimatedCostPerDay || ''}
                                                onChange={(e) => updateEditField(setCarSelections, idx, 'estimatedCostPerDay', parseFloat(e.target.value) || 0)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth size="small" label="Notes"
                                                value={carSelections[idx]?.editData?.notes || ''}
                                                onChange={(e) => updateEditField(setCarSelections, idx, 'notes', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                    </Grid>
                                )}
                            />
                        ))}
                    </SectionWrapper>
                )}

                {/* ═══ ACTIVITIES SECTION ════════════════════ */}
                {plan.activities.length > 0 && (
                    <SectionWrapper
                        title="Activities & Experiences"
                        icon={<Attractions />}
                        color={sectionColors.activities}
                        count={activitySelections.filter(s => s.selected).length}
                        total={plan.activities.length}
                        expanded={expandedSections.includes('activities')}
                        onToggle={() => toggleSection('activities')}
                    >
                        {plan.activities.map((activity, idx) => (
                            <ReviewItemCard
                                key={`activity-${idx}`}
                                selected={activitySelections[idx]?.selected ?? true}
                                editing={activitySelections[idx]?.editing ?? false}
                                onToggle={() => toggleItem(setActivitySelections, idx)}
                                onEdit={() => toggleEdit(setActivitySelections, idx)}
                                onRemove={() => removeItem(setActivitySelections, idx)}
                                color={sectionColors.activities}
                                aiReason={activity.aiReason}
                                renderDisplay={() => (
                                    <>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                            {categoryIcons[activity.category] || '📌'} {activity.title}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {activity.location}
                                            {activity.date ? ` · ${formatDate(activity.date)}` : ''}
                                            {activity.time ? ` at ${activity.time}` : ''}
                                            {activity.duration ? ` · ${activity.duration}` : ''}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: sectionColors.activities, mt: 0.5 }}>
                                            {formatCurrency(activity.estimatedCostPerPerson)}/person
                                        </Typography>
                                        {activity.notes && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                                {activity.notes}
                                            </Typography>
                                        )}
                                    </>
                                )}
                                renderEdit={() => (
                                    <Grid container spacing={1.5}>
                                        <Grid item xs={8}>
                                            <TextField fullWidth size="small" label="Activity Name"
                                                value={activitySelections[idx]?.editData?.title || ''}
                                                onChange={(e) => updateEditField(setActivitySelections, idx, 'title', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField fullWidth size="small" label="Category"
                                                value={activitySelections[idx]?.editData?.category || ''}
                                                onChange={(e) => updateEditField(setActivitySelections, idx, 'category', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={4}>
                                            <TextField fullWidth size="small" label="Date" type="date"
                                                value={activitySelections[idx]?.editData?.date || ''}
                                                onChange={(e) => updateEditField(setActivitySelections, idx, 'date', e.target.value)}
                                                InputLabelProps={{ shrink: true }}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField fullWidth size="small" label="Time"
                                                value={activitySelections[idx]?.editData?.time || ''}
                                                onChange={(e) => updateEditField(setActivitySelections, idx, 'time', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={2}>
                                            <TextField fullWidth size="small" label="$/pp" type="number"
                                                value={activitySelections[idx]?.editData?.estimatedCostPerPerson || ''}
                                                onChange={(e) => updateEditField(setActivitySelections, idx, 'estimatedCostPerPerson', parseFloat(e.target.value) || 0)}
                                                sx={editFieldSx} />
                                        </Grid>
                                        <Grid item xs={3}>
                                            <TextField fullWidth size="small" label="Duration"
                                                value={activitySelections[idx]?.editData?.duration || ''}
                                                onChange={(e) => updateEditField(setActivitySelections, idx, 'duration', e.target.value)}
                                                sx={editFieldSx} />
                                        </Grid>
                                    </Grid>
                                )}
                            />
                        ))}
                    </SectionWrapper>
                )}

                {/* ═══ BUDGET SUMMARY ═══════════════════════ */}
                {plan.budget && mode === 'phase1' && (
                    <SectionWrapper
                        title="Budget Breakdown"
                        icon={<AccountBalanceWallet />}
                        color={sectionColors.budget}
                        expanded={expandedSections.includes('budget')}
                        onToggle={() => toggleSection('budget')}
                    >
                        <Box sx={{ px: 2, pb: 2 }}>
                            <Grid container spacing={1}>
                                {[
                                    { label: 'Flights', value: plan.budget.flights },
                                    { label: 'Hotels', value: plan.budget.hotels },
                                    { label: 'Car Rental', value: plan.budget.carRental },
                                    { label: 'Activities', value: plan.budget.activities },
                                    { label: 'Food & Dining', value: plan.budget.foodEstimate },
                                    { label: 'Miscellaneous', value: plan.budget.miscEstimate },
                                ].filter(item => item.value > 0).map(item => (
                                    <Grid item xs={6} key={item.label}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                                            <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatCurrency(item.value)}</Typography>
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                            <Divider sx={{ my: 1.5 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>AI Estimated Total</Typography>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#FF6B35' }}>
                                    {formatCurrency(plan.budget.total)}
                                </Typography>
                            </Box>

                            {plan.budget.savingsTips && plan.budget.savingsTips.length > 0 && (
                                <Box sx={{ mt: 2, p: 1.5, bgcolor: '#FFF8E1', borderRadius: 2 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 700, color: '#F57F17', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                        <TipsAndUpdates sx={{ fontSize: 16 }} /> Saving Tips
                                    </Typography>
                                    {plan.budget.savingsTips.map((tip, i) => (
                                        <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                            • {tip}
                                        </Typography>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    </SectionWrapper>
                )}

                {/* ═══ TRAVEL TIPS ══════════════════════════ */}
                {plan.travelTips && plan.travelTips.length > 0 && mode === 'phase1' && (
                    <SectionWrapper
                        title="Travel Tips"
                        icon={<Lightbulb />}
                        color={sectionColors.tips}
                        expanded={expandedSections.includes('tips')}
                        onToggle={() => toggleSection('tips')}
                    >
                        <Box sx={{ px: 2, pb: 2 }}>
                            {plan.travelTips.map((tip, i) => (
                                <Typography key={i} variant="body2" color="text.secondary" sx={{ py: 0.3 }}>
                                    💡 {tip}
                                </Typography>
                            ))}
                        </Box>
                    </SectionWrapper>
                )}

                {/* ═══ ITINERARY OVERVIEW ═══════════════════ */}
                {plan.itinerary && plan.itinerary.length > 0 && mode === 'phase1' && (
                    <SectionWrapper
                        title="Daily Themes"
                        icon={<Info />}
                        color="#795548"
                        expanded={expandedSections.includes('itinerary')}
                        onToggle={() => toggleSection('itinerary')}
                    >
                        <Box sx={{ px: 2, pb: 2 }}>
                            {plan.itinerary.map(day => (
                                <Box key={day.dayNumber} sx={{
                                    display: 'flex', gap: 1.5, py: 0.75, alignItems: 'center',
                                }}>
                                    <Chip label={`D${day.dayNumber}`} size="small"
                                        sx={{ width: 36, height: 22, fontSize: '0.7rem', bgcolor: '#EFEBE9', fontWeight: 700 }} />
                                    <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80 }}>
                                        {formatDate(day.date)}
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {day.theme}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                    </SectionWrapper>
                )}

            </DialogContent>

            <Divider />

            {/* ═══ FOOTER ═══════════════════════════════════ */}
            <DialogActions sx={{ px: 3, py: 2, justifyContent: 'space-between' }}>
                <Box>
                    <Typography variant="caption" color="text.secondary">
                        {selectedCount} item{selectedCount !== 1 ? 's' : ''} selected · Est. {formatCurrency(estimatedTotal)}
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1.5 }}>
                    <Button onClick={onClose} sx={{ textTransform: 'none', color: 'text.secondary' }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleApply}
                        disabled={selectedCount === 0}
                        startIcon={<AutoAwesome />}
                        sx={{
                            textTransform: 'none', fontWeight: 700, borderRadius: '24px', px: 3,
                            bgcolor: '#2D6E6A', fontFamily: 'Poppins, sans-serif',
                            '&:hover': { bgcolor: '#009688' },
                        }}
                    >
                        Apply {selectedCount === totalCount ? 'All' : `Selected (${selectedCount})`}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

// ─── Sub-Components ──────────────────────────────────

const editFieldSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5, fontSize: '0.85rem' } };

/** Collapsible section wrapper */
const SectionWrapper: React.FC<{
    title: string;
    icon: React.ReactNode;
    color: string;
    count?: number;
    total?: number;
    expanded: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}> = ({ title, icon, color, count, total, expanded, onToggle, children }) => (
    <Box>
        <Box
            onClick={onToggle}
            sx={{
                display: 'flex', alignItems: 'center', gap: 1.5,
                px: 3, py: 1.5, cursor: 'pointer',
                bgcolor: expanded ? '#fafafa' : 'transparent',
                borderBottom: expanded ? '1px solid #eee' : 'none',
                '&:hover': { bgcolor: '#f5f5f5' },
                transition: 'background-color 0.15s',
            }}
        >
            <Box sx={{ color, display: 'flex', fontSize: 20 }}>{icon}</Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontFamily: 'Poppins, sans-serif', flex: 1 }}>
                {title}
            </Typography>
            {count !== undefined && total !== undefined && (
                <Chip label={`${count}/${total}`} size="small"
                    sx={{
                        height: 22, fontSize: '0.7rem', fontWeight: 600,
                        bgcolor: count > 0 ? '#E8F5E9' : '#FFEBEE',
                        color: count > 0 ? '#2E7D32' : '#C62828',
                    }} />
            )}
            {expanded ? <ExpandLess sx={{ color: '#999' }} /> : <ExpandMore sx={{ color: '#999' }} />}
        </Box>
        <Collapse in={expanded}>
            {children}
        </Collapse>
        {!expanded && <Divider />}
    </Box>
);

/** Individual review item card with select/edit/remove */
const ReviewItemCard: React.FC<{
    selected: boolean;
    editing: boolean;
    onToggle: () => void;
    onEdit: () => void;
    onRemove: () => void;
    color: string;
    aiReason: string;
    renderDisplay: () => React.ReactNode;
    renderEdit: () => React.ReactNode;
}> = ({ selected, editing, onToggle, onEdit, onRemove, color, aiReason, renderDisplay, renderEdit }) => (
    <Box sx={{
        mx: 2, my: 1.5,
        borderRadius: 2,
        border: `1.5px solid ${selected ? color : '#e0e0e0'}`,
        bgcolor: selected ? 'white' : '#fafafa',
        opacity: selected ? 1 : 0.55,
        transition: 'all 0.2s ease',
        overflow: 'hidden',
    }}>
        <Box sx={{ p: 2 }}>
            {/* Top row: checkbox + content + actions */}
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                {/* Selection toggle */}
                <IconButton size="small" onClick={onToggle} sx={{ alignSelf: 'flex-start', mt: -0.25 }}>
                    {selected
                        ? <CheckCircle sx={{ color, fontSize: 22 }} />
                        : <RadioButtonUnchecked sx={{ color: '#ccc', fontSize: 22 }} />
                    }
                </IconButton>

                {/* Content area */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    {editing ? renderEdit() : renderDisplay()}
                </Box>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignSelf: 'flex-start' }}>
                    <Tooltip title={editing ? 'Done editing' : 'Edit'} placement="left">
                        <IconButton size="small" onClick={onEdit}
                            sx={{ color: editing ? '#2D6E6A' : '#bbb', '&:hover': { color: editing ? '#009688' : '#666' } }}>
                            {editing ? <Check sx={{ fontSize: 18 }} /> : <Edit sx={{ fontSize: 18 }} />}
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Remove" placement="left">
                        <IconButton size="small" onClick={onRemove}
                            sx={{ color: '#bbb', '&:hover': { color: '#f44336' } }}>
                            <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {/* AI Reason chip */}
            {aiReason && selected && !editing && (
                <Box sx={{
                    mt: 1.5, ml: 4.5, p: 1, borderRadius: 1.5,
                    bgcolor: '#FFF8F5',
                    border: '1px solid #FFE0D0',
                    display: 'flex', alignItems: 'flex-start', gap: 0.75,
                }}>
                    <AutoAwesome sx={{ color: '#FF6B35', fontSize: 14, mt: 0.25 }} />
                    <Typography variant="caption" sx={{ color: '#B34700', lineHeight: 1.4 }}>
                        {aiReason}
                    </Typography>
                </Box>
            )}
        </Box>
    </Box>
);

export default AiResultsReviewModal;
